// src/hooks/useAssistants.js
import { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders.js';

const BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

export const useAssistants = () => {
  const [assistants, setAssistants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAssistants = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${BASE_URL}/api/ai-assistants`, {
        headers,
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch assistants');
      }

      const data = await response.json();
      setAssistants(data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching assistants:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const createAssistant = async (assistantData) => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${BASE_URL}/api/ai-assistants`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(assistantData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create assistant');
      }

      const newAssistant = await response.json();
      setAssistants(prev => [...prev, newAssistant]);
      return newAssistant;
    } catch (err) {
      setError(err.message);
      console.error('Error creating assistant:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateAssistant = async (id, assistantData) => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${BASE_URL}/api/ai-assistants/${id}`, {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify(assistantData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update assistant');
      }

      const updatedAssistant = await response.json();
      setAssistants(prev =>
        prev.map(a => a.id === id ? updatedAssistant : a)
      );
      return updatedAssistant;
    } catch (err) {
      setError(err.message);
      console.error('Error updating assistant:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteAssistant = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${BASE_URL}/api/ai-assistants/${id}`, {
        method: 'DELETE',
        headers,
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete assistant');
      }

      setAssistants(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      setError(err.message);
      console.error('Error deleting assistant:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getAssistantById = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${BASE_URL}/api/ai-assistants/${id}`, {
        headers,
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch assistant');
      }

      return await response.json();
    } catch (err) {
      setError(err.message);
      console.error('Error fetching assistant:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAssistants();
  }, [fetchAssistants]);

  return {
    assistants,
    loading,
    error,
    fetchAssistants,
    createAssistant,
    updateAssistant,
    deleteAssistant,
    getAssistantById
  };
};
